import React, { useCallback, useEffect, useState } from "react";

import {
  CONNECTED_SERVICE_CATALOG,
  disconnectConnectedService,
  listConnectedServices,
  markConnectedServiceOAuthReturn,
  readConnectedServiceOAuthReturnParams,
  clearConnectedServiceOAuthReturnParams,
  startConnectedServiceOAuth,
} from "../../connectedServices";

function statusLabel(connection) {
  if (connection?.connected) {
    return "Connected";
  }
  return "Not Connected";
}

export function ConnectedServicesSection() {
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyServiceId, setBusyServiceId] = useState(null);
  const [banner, setBanner] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const services = await listConnectedServices();
      const next = {};
      for (const row of services) {
        next[row.serviceId] = row;
      }
      setConnections(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load Connected Services.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const oauthReturn = readConnectedServiceOAuthReturnParams();
    if (oauthReturn) {
      if (oauthReturn.result === "success") {
        setBanner(`${oauthReturn.serviceId === "gmail" ? "Gmail" : oauthReturn.serviceId} connected.`);
      } else {
        setBanner(
          `Could not connect ${oauthReturn.serviceId}. ${oauthReturn.error || "Please try again."}`,
        );
      }
      clearConnectedServiceOAuthReturnParams();
    }
    refresh();
  }, [refresh]);

  const handleConnect = async (serviceId) => {
    setBusyServiceId(serviceId);
    setBanner(null);
    setError(null);
    try {
      markConnectedServiceOAuthReturn("user_preferences");
      const { authUrl } = await startConnectedServiceOAuth(serviceId);
      // Same-tab redirect so the OAuth callback returns into Preferences.
      window.location.assign(authUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start connection.");
      setBusyServiceId(null);
    }
  };

  const handleDisconnect = async (serviceId) => {
    setBusyServiceId(serviceId);
    setBanner(null);
    setError(null);
    try {
      const connection = await disconnectConnectedService(serviceId);
      setConnections((prev) => ({
        ...prev,
        [serviceId]: connection,
      }));
      setBanner("Gmail disconnected.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to disconnect.");
    } finally {
      setBusyServiceId(null);
    }
  };

  return (
    <div className="user-preferences__group">
      <h3 className="user-preferences__heading">Connected Services</h3>
      <p className="user-preferences__hint" style={{ marginTop: 0 }}>
        Connect accounts Ashley can use for voice assistance. Tokens are stored securely on the
        server and are never exposed in the app.
      </p>

      {banner ? (
        <p className="admin-save-confirmation" role="status">
          {banner}
        </p>
      ) : null}
      {error ? (
        <p className="admin-placeholder-note" role="alert">
          {error}
        </p>
      ) : null}

      {CONNECTED_SERVICE_CATALOG.map((service) => {
        const connection = connections[service.id];
        const connected = Boolean(connection?.connected);
        const busy = busyServiceId === service.id || loading;

        return (
          <div key={service.id} className="user-preferences__service-row">
            <div className="user-preferences__service-copy">
              <span className="user-preferences__label">{service.label}</span>
              <p className="user-preferences__hint">
                {statusLabel(connection)}
                {connected && connection?.accountEmail
                  ? ` · ${connection.accountEmail}`
                  : null}
              </p>
              <p className="user-preferences__hint">{service.description}</p>
            </div>
            <div className="user-preferences__service-actions">
              {connected ? (
                <button
                  type="button"
                  className="user-preferences__service-btn user-preferences__service-btn--ghost"
                  disabled={busy}
                  onClick={() => handleDisconnect(service.id)}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  className="user-preferences__service-btn"
                  disabled={busy}
                  onClick={() => handleConnect(service.id)}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
